import { createRouter, createWebHistory } from 'vue-router';
import ApplicationsSettingsLoader from '@core/classes/Applications/ApplicationsSettingsLoader';
import CustomerHelper from '@core/helpers/Customer/customer-helper';

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			path: '/redirect',
			name: 'redirect',
			component: () => import(`@/views/Redirect.vue`),
			//component: Redirect
		},
		{
			path: '/privacy-policy',
			name: 'PrivacyPolicy',
			component: () => import(`@/views/PrivacyPolicy.vue`),
			//component: PrivacyPolicy
		},
		{
			path: '/help',
			name: 'help',
			component: () => import(`@/views/Help.vue`),
			//component: Help
		},
		{
			path: '/meetingroom/',
			component: () => import(`@/components/meeting/MeetingRoomWrapper.vue`),
			name: 'meetingroomWrapper',
			children: [
				//{
				//    path: '', // default child route, could redirect or render something else if needed
				//    redirect: 'inStoreConnection'
				//},
				{
					path: 'inStoreConnection',
					name: 'inStoreConnection',
					component: () => import(`@/components/meeting/InStoreConnection.vue`),
				},
				{
					path: 'disconnected',
					name: 'meetingRoomDisconnected',
					component: () => import(`@/components/meeting/MeetingRoomDisconnected.vue`),
				},
				{
					path: ':code',
					name: 'meetingroomCode',
					component: () => import(`@/components/meeting/MeetingRoom.vue`),
					children: [
						{
							path: 'stand-by',
							name: 'stand-by',
							component: () => import(`@/components/meeting/StandBy.vue`),
						},
						{
							path: 'presentation',
							name: 'presentation',
							component: () => import(`@/components/meeting/videoPresentation/FIMenuVideoPlayer.vue`),
						},
						{
							path: 'coverage-selection',
							name: 'coverage-selection',
							component: () => import(`@/components/meeting/paymentSheet/PaymentSheetWrapper.vue`),
						},
						{
							path: 'paperwork-signing',
							name: 'paperwork-signing',
							component: () => import(`@/components/meeting/FIMenuPaperwork.vue`),
						},
					],
				},
			],
		},
		{
			path: '/cashbashdisplay/',
			name: 'cashbashdisplay',
			component: () => import(`@/views/CashbashDisplay.vue`),
			//component: CashbashDisplay
		},
		{
			path: '/customer-checklist',
			name: 'CustomerChecklist',
			component: () => import(`@/views/CustomerChecklist.vue`),
			//component: CustomerChecklist
		},
		{
			path: '/plaid',
			name: 'PlaidLinkPortal',
			component: () => import(`@/views/PlaidLinkPortal.vue`),
		},
		{
			path: '/plaid/income-verification',
			name: 'PlaidProofOfIncome',
			component: () => import(`@/views/PlaidProofOfIncome.vue`),
		},
		{
			path: '/e-sign',
			name: 'ESign',
			component: () => import(`@/views/UserESignPage.vue`),
			//component: UserESignPage
		},
		{
			path: '/service-lookup',
			name: 'ServiceLookup',
			component: () => import(`@/views/ServiceLookup.vue`),
		},
		{
			path: '/insurance',
			name: 'InsurancePortal',
			component: () => import(`@/views/InsurancePortal.vue`),
		},
		{
			path: '/upload-document',
			name: 'DocumentUploadPortal',
			component: () => import(`@/views/DocumentUploadPortal.vue`),
		},
		//NOTE: This is the first page after the OTP for customer app
		{
			path: '/creditapp/creditappQuestionnaire',
			name: 'creditappQuestionnaire',
			component: () => import(`@/views/CreditAppMain.vue`),
			beforeEnter: ApplicationsSettingsLoader.beforeEnter,
		},
		//NOTE: This is the prefilled data page.
		{
			path: '/creditapp/',
			name: 'creditappLogin',
			component: () => import(`@/views/CreditAppCreateApplication.vue`),
			beforeEnter: ApplicationsSettingsLoader.beforeEnter,
		},
		//NOTE: The OTP page for the customer portal AND the customer application
		{
			path: '/customer-portal/phoneVerification',
			name: 'OTPPage',
			component: () => import(`@/views/OTPPage.vue`),
			beforeEnter: ApplicationsSettingsLoader.beforeEnter,
		},
		{
			//NOTE: After the OTP is entered to get into the customer portal, the customer then gets to this page that chooses where to send them based on logic. This does not have a view at all, its just for pushing/redirecting.
			path: '/customer-portal/redirect-page',
			name: 'CustomerPortal',
			component: () => import(`@/views/customerPortal/CustomerPortal.vue`),
			beforeEnter: ApplicationsSettingsLoader.beforeEnter,
		},
		{
			path: '/customer-portal/no-access',
			name: 'no-access',
			component: () => import(`@/views/customerPortal/NotFoundPage.vue`),
			beforeEnter: ApplicationsSettingsLoader.beforeEnter,
		},
		{
			path: '/customer-portal/:id?',
			name: 'customer-portal',
			beforeEnter: async (to, from, next) => {
				const localStorageId = localStorage.getItem('customerId') ?? null;
				const queryId = to.query.id as string;
				const hasIdInLocalStorage = localStorageId === queryId;

				if (!hasIdInLocalStorage) {
					try {
						const generatedLink = await CustomerHelper.getCustomerAndGenerateLink(queryId);
						window.location.href = generatedLink;
					} catch (error) {
						console.error('error', error);
						next({ name: 'no-access' });
					}
				} else {
					await ApplicationsSettingsLoader.loadApplicationSettings();
					next();
				}
			},
			component: () => import(`@/views/customerPortal/Layout.vue`),
			meta: { requiresCustomerData: true },
			children: [
				{
					path: '',
					name: 'home',
					component: () => import(`@/views/customerPortal/HomePage.vue`),
				},
				{
					path: 'profile',
					name: 'profile',
					component: () => import(`@/views/customerPortal/ProfilePage.vue`),
				},
				{
					path: 'garage',
					name: 'garage',
					component: () => import(`@/views/customerPortal/GaragePage.vue`),
				},
				{
					path: 'identity-verification',
					name: 'identity-verification',
					component: () => import(`@/views/customerPortal/IdentityVerificationPage.vue`),
				},
				{
					path: 'business',
					name: 'business',
					component: () => import(`@/views/customerPortal/BusinessPage.vue`),
				},
				{
					path: 'add-business',
					name: 'add-business',
					component: () => import(`@/views/customerPortal/AddBusinessPage.vue`),
				},
				{
					path: 'add-vehicle',
					name: 'add-vehicle',
					component: () => import(`@/views/customerPortal/AddVehiclePage.vue`),
				},
				{
					path: '*',
					name: 'not-found',
					component: () => import(`@/views/customerPortal/NotFoundPage.vue`),
				},
			],
		},
	],
});

//Preventing navigation to the previous route only if the route does not contain customer portal
router.afterEach((to, from, next) => {
	if (!to.path.includes('/customer-portal')) {
		history.pushState(null, null, location.href);
		// This event always fires when history is changed, does not matter about the if stmt above.
		window.onpopstate = function () {
			if (!window.location.pathname.includes('/customer-portal')) {
				history.go(1);
			}
		};
	}
});
export default router;
